import { c as _c } from "react/compiler-runtime";
import { IcoChevronDown16, IcoDragAndDrop16, IcoPlusSmall16, IcoTrash16 } from '@onefootprint/icons';
import { Button, Card, Dropdown, IconButton, useConfirmationDialog } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { motion, useReducedMotion } from 'framer-motion';
import { type PointerEvent as ReactPointerEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createInlineMarkupRegistry, EditableText } from '@/components/block-primitives';
import trapFieldFocus from '../../components/agent-blocks/block-shell/trap-field-focus';
import { useTaskBlockContext } from '../../components/agent-blocks/task-block-context';
import { useOptionalBuilder } from '../../store/provider';
import useRailReorder from '../use-rail-reorder';
import AddSectionDialog from './add-section-dialog';
import type { ConditionOption } from './condition-options';
import { conditionOptions as getConditionOptions } from './condition-options';
import { newSection, outlineOf, type ReportSection, type ReportSectionKind, sectionHeading, sectionTitle, serializeOutline } from './report-outline';
import SectionExample from './section-example';
import SectionFooter from './section-footer';
import SectionInserter from './section-inserter';
import { KIND_GROUPS, KINDS_IN_GROUP, SECTION_KINDS } from './section-kinds';
const markup = createInlineMarkupRegistry([]);
type ReportBuilderProps = {
  spec: string;
  editable: boolean;
  /** `structural` is anything but typed text: those edits commit from a portal or an unmounting control, so no blur follows to save them. */
  onChange: (spec: string, change: {
    structural: boolean;
  }) => void;
  conditions?: ConditionOption[];
};
const ReportBuilder = t0 => {
  const $ = _c(26);
  const {
    spec,
    editable,
    onChange,
    conditions
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.report"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const [isPicking, setIsPicking] = useState(false);
  const insertIndex = useRef(0);
  const pickAt = index => {
    insertIndex.current = index;
    setIsPicking(true);
  };
  const builder = useOptionalBuilder();
  const [justAdded, setJustAdded] = useState(builder?.state.focusSectionId ?? null);
  const task = useTaskBlockContext();
  let t2;
  if ($[1] !== builder?.state.bundle.nodes || $[2] !== conditions || $[3] !== task?.nodeId) {
    const nodes = builder?.state.bundle.nodes ?? [];
    let t3;
    if ($[5] !== task?.nodeId) {
      t3 = node => node.id === task?.nodeId;
      $[5] = task?.nodeId;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    const taskIndex = nodes.findIndex(t3);
    const availableNodes = nodes.slice(0, taskIndex + 1);
    t2 = conditions ?? getConditionOptions(availableNodes);
    $[1] = builder?.state.bundle.nodes;
    $[2] = conditions;
    $[3] = task?.nodeId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const conditionOptions = t2;
  const outline = outlineOf(spec);
  const canReorder = editable && outline.length > 1;
  let t3;
  if ($[7] !== onChange) {
    t3 = (sections, t4) => {
      const structural = t4 === undefined ? true : t4;
      return onChange(serializeOutline(sections), {
        structural
      });
    };
    $[7] = onChange;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const commit = t3;
  const move = (from, to) => {
    if (to < 0 || to > outline.length - 1) {
      return;
    }
    const next = [...outline];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    commit(next);
  };
  const {
    listRef,
    dragIndex,
    dropIndex,
    startDrag
  } = useRailReorder({
    disabled: !canReorder,
    revision: spec,
    onReorder: move
  });
  const patch = (id, next_0, structural_0) => commit(outline.map(section => section.id === id ? {
    ...section,
    ...next_0
  } : section), structural_0);
  const insertAt = (index_0, kind) => {
    const section_0 = newSection(kind, "");
    setJustAdded(section_0.id);
    commit(outline.toSpliced(index_0, 0, section_0));
  };
  const t4 = "flex flex-col";
  const t5 = outline.length === 0 ? <Card.Root><Card.Empty>{editable ? t(_temp) : t(_temp2)}</Card.Empty></Card.Root> : <div className="flex flex-col gap-3" ref={listRef}>{outline.map((section_1, index_1) => <div className="flex flex-col" key={section_1.id}>{editable && index_1 > 0 && dragIndex === null && <SectionInserter after={sectionTitle(outline[index_1 - 1])} onInsert={() => pickAt(index_1)} />}{dropIndex === index_1 && dragIndex !== null && <span className="h-px w-full bg-accent" />}<OutlineRow canReorder={canReorder} conditions={conditionOptions} dragging={dragIndex === index_1} editable={editable} entering={justAdded === section_1.id} onSettle={() => {
        setJustAdded(null);
        if (builder?.state.focusSectionId === section_1.id) {
          builder.dispatch({
            type: "focusSection",
            id: null
          });
        }
      }} onMove={direction => move(index_1, index_1 + direction)} onPatch={(next_1, structural_1) => patch(section_1.id, next_1, structural_1)} onPointerDown={event => startDrag(event, index_1)} name={sectionTitle(section_1)} onRemove={() => commit(outline.filter(candidate => candidate.id !== section_1.id))} position={index_1 + 1} section={section_1} /></div>)}</div>;
  let t6;
  if ($[9] !== editable || $[10] !== outline || $[11] !== pickAt || $[12] !== t) {
    t6 = editable && <div className={cx("flex justify-center", outline.length > 0 ? "mt-3" : "mt-2")}><Button onClick={() => pickAt(outline.length)} prefixIcon={IcoPlusSmall16} size="compact" variant="secondary">{t(_temp3)}</Button></div>;
    $[9] = editable;
    $[10] = outline;
    $[11] = pickAt;
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setIsPicking(false);
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== editable || $[16] !== insertAt || $[17] !== insertIndex) {
    t8 = kind_0 => {
      if (editable) {
        insertAt(insertIndex.current, kind_0);
      }
    };
    $[15] = editable;
    $[16] = insertAt;
    $[17] = insertIndex;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const t9 = isPicking && editable;
  let t10;
  if ($[19] !== t8 || $[20] !== t9) {
    t10 = <AddSectionDialog onClose={t7} onPick={t8} open={t9} />;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t5 || $[24] !== t6) {
    t11 = <div className={t4}>{t5}{t6}{t10}</div>;
    $[22] = t10;
    $[23] = t5;
    $[24] = t6;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  return t11;
};
type OutlineRowProps = {
  canReorder: boolean;
  section: ReportSection;
  name: string;
  position: number;
  conditions: ConditionOption[];
  dragging: boolean;
  entering: boolean;
  /** Ends the just-added state, which holds the keyboard inside the section. */
  onSettle: () => void;
  editable: boolean;
  onPatch: (next: Partial<ReportSection>, structural: boolean) => void;
  onRemove: () => void;
  onPointerDown: (event: ReactPointerEvent) => void;
  onMove: (direction: -1 | 1) => void;
};
const OutlineRow = t0 => {
  const $ = _c(126);
  const {
    canReorder,
    section,
    name,
    position,
    conditions,
    dragging,
    entering,
    onSettle,
    editable,
    onPatch,
    onRemove,
    onPointerDown,
    onMove
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.report"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const reducedMotion = useReducedMotion();
  const confirm = useConfirmationDialog();
  const sectionRef = useRef(null);
  const isWriting = entering && editable;
  const KindIcon = SECTION_KINDS[section.kind].icon;
  let t2;
  if ($[1] !== confirm || $[2] !== onRemove || $[3] !== section.instruction || $[4] !== t) {
    t2 = () => confirm.open({
      title: t(_temp4),
      description: section.instruction.trim() ? t(_temp5) : t(_temp6),
      variant: "destructive",
      primaryButton: {
        label: t(_temp7),
        onClick: onRemove
      },
      secondaryButton: {
        label: t(_temp8)
      }
    });
    $[1] = confirm;
    $[2] = onRemove;
    $[3] = section.instruction;
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const confirmRemove = t2;
  let t3;
  if ($[6] !== section.kind) {
    t3 = $_4 => $_4.kinds[section.kind].label;
    $[6] = section.kind;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t || $[9] !== t3) {
    t4 = t(t3);
    $[8] = t;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t4) {
    t5 = <span className="text-label-3 text-tertiary">{t4}</span>;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const kindLabel = t5;
  const t6 = canReorder && "transition-opacity group-focus-within/grip:opacity-0 group-hover/row:opacity-0 in-data-force-affordance:opacity-0";
  let t7;
  if ($[13] !== t6) {
    t7 = cx(t6);
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== KindIcon || $[16] !== t7) {
    t8 = <KindIcon className={t7} color="tertiary" />;
    $[15] = KindIcon;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== canReorder || $[19] !== name || $[20] !== onMove || $[21] !== onPointerDown || $[22] !== t) {
    t9 = canReorder && <button aria-keyshortcuts="ArrowUp ArrowDown" aria-label={t(_temp9, {
      title: name
    })} className="absolute inset-0 flex cursor-grab items-center justify-center rounded-sm opacity-0 transition-opacity focus:opacity-100 group-hover/row:opacity-100 in-data-force-affordance:opacity-100" onKeyDown={event => {
      if (event.key !== "ArrowUp" && event.key !== "ArrowDown") {
        return;
      }
      event.preventDefault();
      onMove(event.key === "ArrowUp" ? -1 : 1);
    }} onPointerDown={onPointerDown} type="button"><IcoDragAndDrop16 color="tertiary" /></button>;
    $[18] = canReorder;
    $[19] = name;
    $[20] = onMove;
    $[21] = onPointerDown;
    $[22] = t;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t8 || $[25] !== t9) {
    t10 = <span className="group/grip relative flex size-4 shrink-0 items-center justify-center">{t8}{t9}</span>;
    $[24] = t8;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const kindIcon = t10;
  let t11;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      opacity: 1,
      y: 0
    };
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const t12 = section.instruction;
  const t13 = dragging && "opacity-50";
  let t14;
  if ($[28] !== t13) {
    t14 = cx("group/row relative", t13);
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== entering || $[31] !== reducedMotion) {
    t15 = entering && !reducedMotion ? {
      opacity: 0,
      y: 4
    } : false;
    $[30] = entering;
    $[31] = reducedMotion;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      duration: 0.15,
      ease: "easeOut"
    };
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== isWriting || $[35] !== onSettle) {
    t17 = isWriting ? event_0 => {
      if (!event_0.currentTarget.contains(event_0.relatedTarget)) {
        onSettle();
      }
    } : undefined;
    $[34] = isWriting;
    $[35] = onSettle;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== isWriting || $[38] !== onSettle) {
    t18 = isWriting ? event_1 => trapFieldFocus(event_1, () => {
      onSettle();
      sectionRef.current?.focus();
    }) : undefined;
    $[37] = isWriting;
    $[38] = onSettle;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== editable || $[41] !== kindLabel || $[42] !== name || $[43] !== onPatch || $[44] !== section.kind || $[45] !== t) {
    t19 = editable ? <Dropdown.Root><Dropdown.Trigger ariaLabel={t(_temp10, {
        title: name
      })} className="gap-0.5! rounded-sm transition-opacity hover:opacity-80" role="button" tabIndex={0}>{kindLabel}<IcoChevronDown16 color="tertiary" /></Dropdown.Trigger><Dropdown.Portal><Dropdown.Content align="start" className="max-h-[min(440px,var(--radix-dropdown-menu-content-available-height))] overflow-y-auto p-1" collisionPadding={8} sideOffset={4}>{KIND_GROUPS.map(group => <Dropdown.Group className="[&:not(:first-child)]:mt-2" key={group}><Dropdown.GroupTitle className="flex px-2 py-1 text-label-3 text-tertiary">{t($_7 => $_7.groups[group])}</Dropdown.GroupTitle><Dropdown.RadioGroup className="p-0!" onValueChange={kind => onPatch({
              kind: kind as ReportSectionKind
            }, true)} value={section.kind}>{KINDS_IN_GROUP(group).map(kind_0 => <Dropdown.Item className="h-auto! px-2! py-1.5!" iconLeft={SECTION_KINDS[kind_0].icon} key={kind_0} value={kind_0} variant="radio"><span className="flex flex-col items-start gap-0.5"><span className="text-body-3">{t($_8 => $_8.kinds[kind_0].label)}</span><span className="text-caption-2 text-tertiary">{t($_9 => $_9.kinds[kind_0].purpose)}</span></span></Dropdown.Item>)}</Dropdown.RadioGroup></Dropdown.Group>)}</Dropdown.Content></Dropdown.Portal></Dropdown.Root> : kindLabel;
    $[40] = editable;
    $[41] = kindLabel;
    $[42] = name;
    $[43] = onPatch;
    $[44] = section.kind;
    $[45] = t;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== kindIcon || $[48] !== t19) {
    t20 = <span className="flex items-center gap-1">{kindIcon}{t19}</span>;
    $[47] = kindIcon;
    $[48] = t19;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== confirmRemove || $[51] !== editable || $[52] !== name || $[53] !== t) {
    t21 = editable && <IconButton aria-label={t(_temp11, {
      title: name
    })} className="ml-auto" color="tertiary" icon={IcoTrash16} onClick={confirmRemove} size="tiny" />;
    $[50] = confirmRemove;
    $[51] = editable;
    $[52] = name;
    $[53] = t;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== t20 || $[56] !== t21) {
    t22 = <div className="mb-1 flex h-6 items-center gap-2 pr-1 pl-2">{t20}{t21}</div>;
    $[55] = t20;
    $[56] = t21;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  const t23 = entering && editable;
  const t24 = section.title && "text-primary";
  let t25;
  if ($[58] !== t24) {
    t25 = cx("-ml-1 w-fit min-w-8 rounded-sm px-1 py-0.5 text-label-1 opacity-100! transition-colors duration-100 hover:bg-hover in-data-force-affordance:bg-hover", t24);
    $[58] = t24;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== t) {
    t26 = t(_temp12);
    $[60] = t;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== onPatch) {
    t27 = title => onPatch({
      title: title.trim()
    }, false);
    $[62] = onPatch;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== t) {
    t28 = t(_temp13);
    $[64] = t;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  let t29;
  if ($[66] !== editable || $[67] !== section.title || $[68] !== t23 || $[69] !== t25 || $[70] !== t26 || $[71] !== t27 || $[72] !== t28) {
    t29 = <EditableText as="h3" autoFocus={t23} className={t25} editable={editable} label={t26} onSubmit={t27} optional={true} placeholder={t28} registry={markup} value={section.title} />;
    $[66] = editable;
    $[67] = section.title;
    $[68] = t23;
    $[69] = t25;
    $[70] = t26;
    $[71] = t27;
    $[72] = t28;
    $[73] = t29;
  } else {
    t29 = $[73];
  }
  let t30;
  if ($[74] !== name || $[75] !== t) {
    t30 = t(_temp14, {
      title: name
    });
    $[74] = name;
    $[75] = t;
    $[76] = t30;
  } else {
    t30 = $[76];
  }
  let t31;
  if ($[77] !== onPatch) {
    t31 = instruction => onPatch({
      instruction
    }, false);
    $[77] = onPatch;
    $[78] = t31;
  } else {
    t31 = $[78];
  }
  let t32;
  if ($[79] !== section.kind || $[80] !== t) {
    t32 = section.kind === "custom" ? t(_temp15) : t(_temp16);
    $[79] = section.kind;
    $[80] = t;
    $[81] = t32;
  } else {
    t32 = $[81];
  }
  let t33;
  if ($[82] !== editable || $[83] !== section.instruction || $[84] !== t30 || $[85] !== t31 || $[86] !== t32) {
    t33 = <EditableText as="div" block={true} className="-ml-1 min-h-6 rounded-sm px-1 py-0.5 text-body-2 text-primary opacity-100! transition-colors duration-100 hover:bg-hover in-data-force-affordance:bg-hover [&:empty]:text-quaternary" editable={editable} label={t30} multiline={true} onSubmit={t31} optional={true} placeholder={t32} registry={markup} value={section.instruction} />;
    $[82] = editable;
    $[83] = section.instruction;
    $[84] = t30;
    $[85] = t31;
    $[86] = t32;
    $[87] = t33;
  } else {
    t33 = $[87];
  }
  let t34;
  if ($[88] !== position || $[89] !== section || $[90] !== t) {
    t34 = section.kind !== "custom" && <div className="mt-4.5 flex flex-col gap-2" data-outline-skip={true}><p className="text-caption-2 text-quaternary">{t(_temp17)}</p>{section.kind === "narrative" ? <div className="rounded-lg border border-tertiary bg-primary px-5 py-4"><SectionExample heading={sectionHeading(section, position)} kind="narrative" /></div> : <SectionExample kind={section.kind} />}</div>;
    $[88] = position;
    $[89] = section;
    $[90] = t;
    $[91] = t34;
  } else {
    t34 = $[91];
  }
  let t35;
  if ($[92] !== t33 || $[93] !== t34) {
    t35 = <div className="flex flex-col gap-1.5">{t33}{t34}</div>;
    $[92] = t33;
    $[93] = t34;
    $[94] = t35;
  } else {
    t35 = $[94];
  }
  let t36;
  if ($[95] !== t29 || $[96] !== t35) {
    t36 = <div className="flex flex-col gap-3">{t29}{t35}</div>;
    $[95] = t29;
    $[96] = t35;
    $[97] = t36;
  } else {
    t36 = $[97];
  }
  let t37;
  let t38;
  if ($[98] !== onPatch) {
    t37 = when => onPatch({
      when
    }, true);
    t38 = fallback => onPatch({
      fallback
    }, false);
    $[98] = onPatch;
    $[99] = t37;
    $[100] = t38;
  } else {
    t37 = $[99];
    t38 = $[100];
  }
  let t39;
  if ($[101] !== conditions || $[102] !== editable || $[103] !== name || $[104] !== section.fallback || $[105] !== section.when || $[106] !== t37 || $[107] !== t38) {
    t39 = <SectionFooter conditions={conditions} editable={editable} fallback={section.fallback} onConditionChange={t37} onFallbackChange={t38} title={name} when={section.when} />;
    $[101] = conditions;
    $[102] = editable;
    $[103] = name;
    $[104] = section.fallback;
    $[105] = section.when;
    $[106] = t37;
    $[107] = t38;
    $[108] = t39;
  } else {
    t39 = $[108];
  }
  let t40;
  if ($[109] !== t36 || $[110] !== t39) {
    t40 = <Card.Body className="flex flex-col px-4 pt-3 pb-4">{t36}{t39}</Card.Body>;
    $[109] = t36;
    $[110] = t39;
    $[111] = t40;
  } else {
    t40 = $[111];
  }
  let t41;
  if ($[112] !== t22 || $[113] !== t40) {
    t41 = <Card.Root>{t22}{t40}</Card.Root>;
    $[112] = t22;
    $[113] = t40;
    $[114] = t41;
  } else {
    t41 = $[114];
  }
  let t42;
  if ($[115] !== name || $[116] !== t17 || $[117] !== t18 || $[118] !== t41) {
    t42 = <section aria-label={name} className="outline-none" onBlur={t17} onKeyDownCapture={t18} ref={sectionRef} tabIndex={-1}>{t41}</section>;
    $[115] = name;
    $[116] = t17;
    $[117] = t18;
    $[118] = t41;
    $[119] = t42;
  } else {
    t42 = $[119];
  }
  let t43;
  if ($[120] !== name || $[121] !== section.instruction || $[122] !== t14 || $[123] !== t15 || $[124] !== t42) {
    t43 = <motion.div animate={t11} data-outline={name} data-outline-preview={t12} className={t14} initial={t15} transition={t16}>{t42}</motion.div>;
    $[120] = name;
    $[121] = section.instruction;
    $[122] = t14;
    $[123] = t15;
    $[124] = t42;
    $[125] = t43;
  } else {
    t43 = $[125];
  }
  return t43;
};
export default ReportBuilder;
function _temp($) {
  return $.emptyOutline;
}
function _temp2($_0) {
  return $_0.noSections;
}
function _temp3($_1) {
  return $_1.addSection;
}
function _temp4($) {
  return $.removeConfirmTitle;
}
function _temp5($_0) {
  return $_0.removeConfirmWritten;
}
function _temp6($_1) {
  return $_1.removeConfirmEmpty;
}
function _temp7($_2) {
  return $_2.removeConfirm;
}
function _temp8($_3) {
  return $_3.removeCancel;
}
function _temp9($_5) {
  return $_5.reorderAria;
}
function _temp10($_6) {
  return $_6.kindAria;
}
function _temp11($_10) {
  return $_10.removeAria;
}
function _temp12($_11) {
  return $_11.titleAria;
}
function _temp13($_12) {
  return $_12.titlePlaceholder;
}
function _temp14($_13) {
  return $_13.instructionAria;
}
function _temp15($_14) {
  return $_14.customInstructionPlaceholder;
}
function _temp16($_15) {
  return $_15.instructionPlaceholder;
}
function _temp17($_16) {
  return $_16.exampleCaption;
}